import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { useMutation } from '@apollo/client';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { UPDATE_CAR, GET_CAR } from '../../services/carService/queries';
import { toast } from 'react-toastify';
import { AddressInput, LocationData } from '../addressInput';

const Container = styled.div`
  ${tw`w-full`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold mb-4`}
`;

const MapContainer = styled.div`
  ${tw`w-full mb-4`}
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
`;

const AddressInputContainer = styled.div`
  ${tw`mb-4`}
`;

const Label = styled.label`
  ${tw`block text-text-secondary font-medium mb-2`}
`;

const InfoText = styled.p`
  ${tw`text-sm text-text-secondary mb-4`}
`;

const ButtonContainer = styled.div`
  ${tw`flex justify-end gap-3 mt-6`}
`;

const Button = styled.button<{ disabled?: boolean }>`
  ${tw`px-4 py-2 rounded-md font-medium transition-colors max-w-full min-w-0 box-border`}
  ${props =>
    props.disabled
      ? tw`bg-gray-300 text-text-secondary cursor-not-allowed`
      : tw`bg-black text-white`}
  ${props =>
    !props.disabled &&
    css`
      &:hover {
        ${tw`bg-blue-700`}
      }
    `}
`;

interface CarLocationProps {
  carId: string;
  latitude?: number | null;
  longitude?: number | null;
}

const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = ['places'];

const defaultCenter = {
  lat: 37.7749,
  lng: -122.4194,
};

export default function CarLocation({ carId, latitude, longitude }: CarLocationProps) {
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [address, setAddress] = useState<string>('');
  const [isDirty, setIsDirty] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey || '',
    libraries,
  });

  // Initialize position from props
  useEffect(() => {
    if (latitude && longitude) {
      const position = { lat: latitude, lng: longitude };
      setMarkerPosition(position);
      setMapCenter(position);
    } else {
      setMarkerPosition(null);
    }
  }, [latitude, longitude]);

  const [updateCar, { loading }] = useMutation(UPDATE_CAR, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
    onCompleted: () => {
      toast.success('Location updated successfully');
      setIsDirty(false);
    },
    onError: error => {
      toast.error(`Failed to update location: ${error.message}`);
    },
  });

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const handleMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const newPosition = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      setMarkerPosition(newPosition);
      setMapCenter(newPosition);
      setIsDirty(true);
    }
  }, []);

  const handleLocationSelect = useCallback((location: LocationData) => {
    const position = {
      lat: location.latitude,
      lng: location.longitude,
    };
    setMarkerPosition(position);
    setMapCenter(position);
    setAddress(location.formattedAddress || location.address);
    setIsDirty(true);
  }, []);

  const handleAddressChange = useCallback((newAddress: string) => {
    setAddress(newAddress);
  }, []);

  const handleSave = async () => {
    if (!markerPosition) {
      toast.error('Please select a location on the map');
      return;
    }

    try {
      await updateCar({
        variables: {
          id: carId,
          updateCarInput: {
            latitude: markerPosition.lat,
            longitude: markerPosition.lng,
          },
        },
      });
    } catch (error) {}
  };

  const handleReset = () => {
    if (latitude && longitude) {
      const position = { lat: latitude, lng: longitude };
      setMarkerPosition(position);
      setMapCenter(position);
    } else {
      setMarkerPosition(null);
      setMapCenter(defaultCenter);
    }
    setAddress('');
    setIsDirty(false);
  };

  if (loadError) {
    return (
      <Container>
        <Title>Car Location</Title>
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-500">Error loading map. Please check your API key.</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Location</Title>
      <InfoText>
        Search for an address or drag the pin on the map to set the car's specific location. This
        location is used for precise location-based searches.
      </InfoText>

      <AddressInputContainer>
        <Label>Address</Label>
        <AddressInput
          value={address}
          placeholder="Enter an address..."
          onLocationSelect={handleLocationSelect}
          onLocationChange={handleAddressChange}
        />
      </AddressInputContainer>

      <MapContainer>
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={markerPosition ? 15 : 10}
            onLoad={handleMapLoad}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
            }}
          >
            {markerPosition && (
              <Marker
                position={markerPosition}
                draggable={true}
                onDragEnd={handleMarkerDragEnd}
                title="Car location"
              />
            )}
          </GoogleMap>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500">Loading map...</div>
          </div>
        )}
      </MapContainer>

      <ButtonContainer>
        {isDirty && (
          <Button onClick={handleReset} disabled={loading}>
            Reset
          </Button>
        )}
        <Button onClick={handleSave} disabled={loading || !isDirty || !markerPosition}>
          {loading ? 'Saving...' : 'Save Location'}
        </Button>
      </ButtonContainer>
    </Container>
  );
}
